import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import DashboardShell from '../../components/layout/DashboardShell';
import Button from '../../components/marketing/Button';
import ContractCheckerSuccessView from '../../components/contractChecker/ContractCheckerSuccessView';
import { readAndClearContractTextCarryover } from '../../lib/contractChecker/carryoverStorage.js';
import { stitchedFullDraftFromRewrites } from '../../lib/brandDealChecker/normalizeRewriteResponse';
import {
  PERSONALIZED_ANALYZE_LIMIT_MESSAGE,
  PERSONALIZED_REWRITE_LIMIT_MESSAGE,
} from '../../lib/brandDealChecker/personalizedRateLimitCopy.js';

const MAX_CHARS = 15000;

export default function ContractToolsPage({ onNavigate }) {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [requestError, setRequestError] = useState('');
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewriteError, setRewriteError] = useState('');
  const [rewriteData, setRewriteData] = useState(null);

  useEffect(() => {
    const carried = readAndClearContractTextCarryover();
    if (carried) setText(carried);
  }, []);

  const charCount = text.length;
  const overLimit = charCount > MAX_CHARS;

  const runAnalyze = useCallback(async () => {
    setRequestError('');
    setResult(null);
    setRewriteOpen(false);
    setRewriteLoading(false);
    setRewriteError('');
    setRewriteData(null);
    const trimmed = text.trim();
    if (!trimmed) {
      setRequestError('Please paste your agreement or deal text.');
      return;
    }
    if (trimmed.length > MAX_CHARS) {
      setResult({
        status: 'too_long',
        message:
          'This text is too long for V1. Please paste the most relevant sections or shorten the agreement (up to 15,000 characters).',
      });
      return;
    }
    if (!user) {
      setRequestError('Sign in to run personalized analysis.');
      return;
    }

    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/brand-deal-checker/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: trimmed, mode: 'personalized' }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.status === 'rate_limited') {
        setResult({
          status: 'rate_limited',
          message: data.message || PERSONALIZED_ANALYZE_LIMIT_MESSAGE,
        });
        return;
      }
      if (!res.ok && !data.status) {
        const errMsg =
          typeof data.message === 'string'
            ? data.message
            : typeof data.error === 'string'
              ? data.error
              : data.error && typeof data.error.message === 'string'
                ? data.error.message
                : null;
        setRequestError(errMsg || 'Something went wrong. Please try again.');
        return;
      }
      setResult(data);
    } catch {
      setRequestError('Network error. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [text, user]);

  const generateSaferRevision = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || !result || result.status !== 'success' || !user) return;
    setRewriteOpen(true);
    setRewriteError('');
    setRewriteLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/brand-deal-checker/rewrite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode: 'personalized',
          contractText: trimmed,
          red_flags: Array.isArray(result.red_flags) ? result.red_flags : [],
          analysisContext: {
            score: result.score,
            risk_level: result.risk_level,
            quick_verdict: result.quick_verdict,
          },
        }),
      });
      let data = await res.json().catch(() => ({}));
      if (data.status === 'rate_limited') {
        setRewriteData(null);
        setRewriteError(data.message || PERSONALIZED_REWRITE_LIMIT_MESSAGE);
        return;
      }
      if (data.status !== 'success' || !Array.isArray(data.rewrites)) {
        setRewriteData(null);
        setRewriteError(data.message || 'Could not generate revisions. Try again.');
        return;
      }
      const fullOk = typeof data.full_rewritten_contract === 'string' && data.full_rewritten_contract.trim().length > 0;
      if (!fullOk && data.rewrites.length > 0) {
        data = {
          ...data,
          full_rewritten_contract: stitchedFullDraftFromRewrites(data.rewrites),
        };
      } else if (!fullOk) {
        setRewriteData(null);
        setRewriteError('Could not load your personalized full draft. Try again.');
        return;
      }
      setRewriteData(data);
    } catch {
      setRewriteData(null);
      setRewriteError('Network error. Check your connection and try again.');
    } finally {
      setRewriteLoading(false);
    }
  }, [text, result, user]);

  const resetAnother = () => {
    setResult(null);
    setRequestError('');
    setText('');
    setRewriteOpen(false);
    setRewriteLoading(false);
    setRewriteError('');
    setRewriteData(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <DashboardShell onNavigate={onNavigate}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Contract Tools</p>
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-3">Analyze contracts with creator context</h1>
        <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-8 max-w-2xl">
          Get contract feedback informed by your InfluenceHub profile, including your platform, audience, niche, and other
          available creator details.
        </p>

        {!result && (
          <>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              This tool provides general informational feedback only and is not legal advice.
            </p>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3 leading-relaxed">
                Do not paste bank details, home addresses, or highly sensitive personal information.
              </p>
              <label htmlFor="contract-tools-text" className="sr-only">
                Agreement text
              </label>
              <textarea
                id="contract-tools-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={14}
                placeholder="Paste agreement or deal terms here..."
                className="w-full min-h-[280px] sm:min-h-[320px] px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base leading-relaxed resize-y"
                disabled={loading}
                autoComplete="off"
                spellCheck="true"
              />
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
                <span className={`text-sm ${overLimit ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
                </span>
                <Button
                  type="button"
                  size="lg"
                  className="w-full sm:w-auto min-h-[48px]"
                  onClick={runAnalyze}
                  disabled={loading || overLimit}
                >
                  {loading ? 'Analyzing…' : 'Run personalized analysis'}
                </Button>
              </div>
            </div>
          </>
        )}

        {requestError && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm" role="alert">
            {requestError}
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {result.status === 'too_long' && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 text-sm leading-relaxed">{result.message}</div>
            )}
            {result.status === 'unsupported_language' && (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 text-sm leading-relaxed">
                {result.message || 'This V1 currently works best with English-language contracts.'}
              </div>
            )}
            {result.status === 'not_contract' && (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 text-sm leading-relaxed">{result.message}</div>
            )}
            {result.status === 'rate_limited' && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 text-sm leading-relaxed">{result.message}</div>
            )}
            {result.status === 'error' && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm leading-relaxed space-y-3">
                <p>{result.message || 'Something went wrong. Please try again.'}</p>
                {result.debug_detail ? (
                  <pre className="text-xs whitespace-pre-wrap break-words bg-white/80 border border-red-100 rounded-lg p-3 text-gray-800 font-mono">
                    {result.debug_detail}
                  </pre>
                ) : null}
              </div>
            )}

            {result.status === 'success' && (
              <ContractCheckerSuccessView
                result={result}
                rewriteOpen={rewriteOpen}
                rewriteLoading={rewriteLoading}
                rewriteError={rewriteError}
                rewriteData={rewriteData}
                generateSaferRevision={generateSaferRevision}
                variant="personalized"
                rewriteExperience="creator"
                rewriteSignupHref="/signup"
              />
            )}

            <p className="text-sm text-gray-600 text-center leading-relaxed px-2">
              Consider consulting a qualified attorney before signing any agreement.
            </p>

            <div className="flex justify-center pb-8">
              <button
                type="button"
                onClick={resetAnother}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline"
              >
                Analyze another contract
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
